<template>
    <div class="relative">
        <div class="flex p-0" :class="[
            props.class,
            (touched && validationError
                ? 'border-red-500 focus:ring-red-500'
                : (touched && isValid
                    ? ' border-green-500 focus:ring-green-500'
                    : ' focus:ring-indigo-500'
                )
            ) 
        ]">
            <div class="" ref="dropdownRef">
                <button :class="props.innerClass" type="button" @click="isDropdownOpen = !isDropdownOpen"
                    class="flex h-full items-center outline-none focus-visible:outline-none focus-visible:border-none gap-1 pl-4">
                    <span class="flex items-center text-lg h-6">{{ selectedCountry.flag }}</span>
                    <span class="flex items-center h-6">{{ selectedCountry.dialCode }}</span>
                    <ChevronDown class="text-gray-400 transition-transform" :size="20"
                        :class="{ 'rotate-180': isDropdownOpen }" />
                </button>

                <transition enter-active-class="transition duration-100 ease-out "
                    enter-from-class="transform scale-95 opacity-0" enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100" leave-to-class="transform scale-95 opacity-0">
                    <div v-if="isDropdownOpen" :class="props.class"
                        class="bg-card absolute top-full p-0 left-0 mt-2 max-w-60 rounded-md shadow-2xl border border-gray-200/20 z-50 max-h-96 h-auto overflow-hidden">
                        <div class="p-3 border-b border-gray-200/20">
                            <div class="relative">
                                <Search :size="18" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input v-model="searchQuery" 
                                    type="text" 
                                    placeholder="Search country..."
                                    class="w-full pl-10 pr-4 py-2 border border-gray-300/20 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-transparent outline-none" />
                            </div>
                        </div> 

                        <div class="overflow-y-auto max-h-80 custom-scrollbar">
                            <button v-for="country in filteredCountries" :key="country.code"
                                @click="handleCountrySelect(country)"
                                type="button"
                                class="w-full flex items-center gap-3 px-4 py-1 hover:bg-indigo-50/10 transition-colors"
                                :class="{ 
                                    'bg-primary-500 text-on-primary': (selectedCountry.code === country.code) 
                                }">
                                <span class="text-md">{{ country.flag }}</span>
                                <div class="flex-1 flex justify-between  text-left">
                                    <div class="font-normal line-clamp-1 me-3">{{ country.name }}</div>
                                    <div class="text-sm text-on-primary text-right">({{ country.dialCode }})</div>
                                </div>
                                <Check v-if="(selectedCountry.code === country.code)" 
                                    :size="20"
                                    class="text-on-primary"/>
                            </button>

                            <div v-if="(filteredCountries.length === 0)" class="px-4 py-8 text-center text-gray-500">
                                No countries found
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <div class="flex-1 relative">
                <div class="relative h-full">
                    <input ref="formattedNumberInput" 
                        @input="handlePhoneChange($event.target as EventTarget)" 
                        @keypress="handleKeyPress"
                        @blur="handleBlur" 
                        type="tel"
                        class="w-full h-full pl-1 pr-10 rounded-lg focus:outline-none bg-transparent placeholder:text-body/30"
                        :class="props.innerClass" 
                        :placeholder="selectedCountry.placeholder" 
                        :value="formattedNumber" />

                    <div v-if="touched" class="absolute right-3 top-1/2 -translate-y-1/2">
                        <Check v-if="isValid" :size="20" class="text-green-500" />
                        <X v-else-if="validationError" :size="20" class="text-red-500" />
                    </div>
                </div>
            </div>
        </div>
    </div>
    <p v-if="touched && validationError" class="mt-1 text-sm text-red-600 flex items-center gap-1">
        <X :size="14" /> {{ validationError }}
    </p>
    <p v-else-if="touched && isValid" class="mt-1 text-sm text-green-600 flex items-center gap-1">
        <Check :size="14" /> Valid phone number
    </p>
    <p v-else class="mt-1 text-sm text-gray-500">
        Format: {{ selectedCountry.format.replace(/#/g, '0') }}
    </p>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, reactive } from 'vue';
import { Phone, ChevronDown, Check, X, Search } from 'lucide-vue-next';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import { getCountryInfo } from './countries';
import parsePhoneNumberFromString, { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';

countries.registerLocale(en);

interface CountryInfo {
    code: string
    name: string
    dialCode: string
    flag: string
    pattern: RegExp
    placeholder: string
    format: string
}

interface Props {
    modelValue?: string | null;
    class?: string;
    innerClass?: string;
    defaultCounty?: string;
    placeholder?: string;
    cleanAfterClean?: boolean; 
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    class: '',
    innerClass: 'py-2',
    defaultCounty: "TZ",
    cleanAfterClean: false,
});

const emit = defineEmits<{
    (e: 'blur', value: string | null): void;
    (e: 'change', value: string | null): void;
    (e: 'update:modelValue', value: string | null): void;
}>();

const countryList = reactive<CountryInfo[]>([]);
for (const code in countries.getAlpha2Codes()) {
    const info = getCountryInfo(code);
    if(info) {
        countryList.push(info);
    }
}

const defaultCounty = computed(() => {
    var c = countryList.filter((e) => (e.code == props.defaultCounty))[0];
    if (!c) {
        c = countryList.filter((e) => (e.code == "TZ"))[0];
    }

    return c;
})

// 2. State Management
const selectedCountry = ref(defaultCounty.value);
const formattedNumberInput = ref<HTMLInputElement | null>(null);
const phoneNumber = ref(props.modelValue); // Stores raw digits only
const isDropdownOpen = ref(false);
const searchQuery = ref('');
const validationError = ref('');
const touched = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const setCurrentCountry = (code: string) => {
    for(const e of countryList) {
        if(e.dialCode == `+${code}` || e.code == code) {
            selectedCountry.value = e;
        }
    }
}

const resetPhoneNumber = (value: any) => {
    if(!window.isEmpty(value)) {
        if(!value.startsWith("+")) value = `+${value}`;
        var v = parsePhoneNumberFromString(value)!;

        if(v) { 
            phoneNumber.value = v.formatNational();
            setCurrentCountry(v.country || "");
        }
    }
}

// 3. Computed Properties
const filteredCountries = computed(() => {
    const query = searchQuery.value.toLowerCase();

    return countryList.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.dialCode.includes(query) ||
        c.code.toLowerCase().includes(query)
    );
}); 

const fullPhoneNumber = computed(() => {
    let code: any = selectedCountry.value.code as CountryCode;
    let number = phoneNumber.value || "";

    if(number.startsWith("+")) { code = undefined; }

    var v = parsePhoneNumberFromString(number, code)!;

    return (v)? v.number: "";
});

const formattedNumber = computed(() => {
    return applyFormat(phoneNumber.value);
});

const isValid = computed(() => {
    return isValidPhoneNumber(fullPhoneNumber.value);
});

// 4. Logic Functions
const applyFormat = (value: string | null) => {
    let formatted = value || "";
    let code: any = selectedCountry.value.code as CountryCode;

    if(formatted.startsWith("+")) {
        code = undefined;
    }

    let v = parsePhoneNumberFromString(formatted, code)!;
    
    if(v && v.country) {
        if(selectedCountry.value.code != v.country) {
            setCurrentCountry(v.country || selectedCountry.value.code)
        }
        
        formatted = v.nationalNumber;
    }

    return formatted;
}

const validatePhone = (value: string | null) => {
    const cleanNumber = (value ? value : '').replace(/\D/g, '');
    if (!cleanNumber) return 'Phone number is required';

    console.dev("cleanNumber", cleanNumber)
    if (!selectedCountry.value.pattern.test(cleanNumber)) {
        return `Invalid phone number for ${selectedCountry.value.name}`;
    }

    return '';
};

const handleKeyPress = (e: KeyboardEvent) => {
    if(["Enter",",",";"].includes(e.key)) {
        if(props.cleanAfterClean) {
            touched.value = true;
            validationError.value = validatePhone(fullPhoneNumber.value);
        }

        if(isValid.value && fullPhoneNumber.value != "") {
            handleBlur(e);
        }
    }
}

const handlePhoneChange = (target: EventTarget) => {
    const input = (target as HTMLInputElement).value;
    phoneNumber.value = input.replace(/[^0-9+]/g, '');
    
    if (touched.value) validationError.value = validatePhone(fullPhoneNumber.value);

    emit("change", fullPhoneNumber.value)
}

const handleBlur = (e: Event) => {
    if(isValid.value) {
        emit("blur", fullPhoneNumber.value)
    }
 
    if(props.cleanAfterClean && isValid.value) {
        const input = (e.target as HTMLInputElement);
        
        input.value = "";
        phoneNumber.value = "";
        touched.value = false;
        validationError.value = "";
    }
}

const handleCountrySelect = (country: typeof countryList[0]) => {
    selectedCountry.value = country;
    isDropdownOpen.value = false;
    searchQuery.value = '';
    if (touched.value && phoneNumber.value) {
        validationError.value = validatePhone(phoneNumber.value);
    }

    let target = document.createElement("INPUT") as HTMLInputElement;
    target.value = phoneNumber.value || '';

    handlePhoneChange(target);
}

const handleSubmit = () => {
    touched.value = true;
    const error = validatePhone(phoneNumber.value);
    if (error) {
        validationError.value = error;
    } else {
        alert(`Phone number submitted: ${fullPhoneNumber.value}`);
    }
}

// 5. Click Outside Handler
const handleClickOutside = (e: MouseEvent) => {
    if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
        isDropdownOpen.value = false;
        searchQuery.value = '';
    }
}

resetPhoneNumber(props.modelValue!);
handleCountrySelect(selectedCountry.value);

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

watch(fullPhoneNumber, (v2, v1) => {
    emit("change", v2);
    emit("update:modelValue", v2);
})

watch(formattedNumber, (v2, v1) => {
    if (formattedNumberInput.value) {
        // formattedNumberInput.value.value = formattedNumber.value;
    }
})

watch(()=>props.modelValue, (v2, v1)=>{
    if(v1 !== v2) {
        resetPhoneNumber(v2);
        handleCountrySelect(selectedCountry.value);
    }
})
</script>

<style lang="scss" scoped>
.text-2xl {
    line-height: 1.5rem !important;
}
</style>